import React, { useCallback, useEffect, useState } from "react";
import { Table, Button, Input, Space, Popconfirm, Alert, Tag, Typography, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faRotateRight, faTrash } from "@fortawesome/free-solid-svg-icons";
import logApi from "../../../api/log-api";

const { Text } = Typography;

// Tab "Chặn IP" trong trang /logs. Chặn ở tầng middleware trước rate limit, nên IP trong
// danh sách này không vào được BẤT KỲ endpoint /api nào.
//
// Server là nơi giữ luật an toàn (IP hệ thống + IP của chính người đang thao tác), FE chỉ
// hiển thị lại thông báo. KHÔNG tự chặn ở FE để tránh hai nơi lệch luật.
function BlockedIpTab() {
    const [entries, setEntries] = useState([]);
    const [currentIp, setCurrentIp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [ip, setIp] = useState("");
    const [note, setNote] = useState("");

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await logApi.getBlockedIps();
            setEntries(data.data?.entries || []);
            setCurrentIp(data.data?.currentIp || null);
        } catch (err) {
            message.error(err?.response?.data?.message || "Không tải được danh sách chặn");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchList(); }, [fetchList]);

    const handleBlock = async () => {
        const value = ip.trim();
        if (!value) return;
        setSaving(true);
        try {
            const { data } = await logApi.blockIp(value, note.trim());
            setEntries(data.data?.entries || []);
            setIp("");
            setNote("");
            message.success(`Đã chặn ${value}`);
        } catch (err) {
            // Server trả lý do cụ thể: IP hệ thống / IP của bạn / trùng / sai định dạng.
            message.error(err?.response?.data?.message || "Không chặn được IP này");
        } finally {
            setSaving(false);
        }
    };

    const handleUnblock = async (value) => {
        try {
            const { data } = await logApi.unblockIp(value);
            setEntries(data.data?.entries || []);
            message.success(`Đã gỡ chặn ${value}`);
        } catch (err) {
            message.error(err?.response?.data?.message || "Không gỡ được IP này");
        }
    };

    const columns = [
        {
            title: "IP",
            dataIndex: "ip",
            width: 200,
            render: (value) => <Text strong copyable>{value}</Text>,
        },
        {
            title: "Ghi chú",
            dataIndex: "note",
            render: (value) => value || <Text type="secondary">—</Text>,
        },
        {
            // Cột trả lời "chặn có ăn không". Số gộp mỗi phút nên có thể trễ chút và là
            // ƯỚC LƯỢNG (api chạy 3 process, xem ip-blocklist-service.js).
            title: "Đã chặn",
            dataIndex: "hits",
            width: 110,
            align: "right",
            sorter: (a, b) => (a.hits || 0) - (b.hits || 0),
            render: (value) =>
                value ? (
                    <Text strong>{Number(value).toLocaleString("vi-VN")} lần</Text>
                ) : (
                    <Text type="secondary">chưa có</Text>
                ),
        },
        {
            title: "Chặn gần nhất",
            dataIndex: "lastHitAt",
            width: 165,
            render: (value) =>
                value ? new Date(value).toLocaleString("vi-VN") : <Text type="secondary">—</Text>,
        },
        {
            title: "Thêm lúc",
            dataIndex: "createdAt",
            width: 165,
            render: (value) => (value ? new Date(value).toLocaleString("vi-VN") : "—"),
        },
        {
            title: "Bởi",
            dataIndex: "createdBy",
            width: 90,
            render: (value) => (value ? `#${value}` : <Text type="secondary">—</Text>),
        },
        {
            title: "",
            key: "action",
            width: 110,
            render: (_, row) => (
                <Popconfirm
                    title={`Gỡ chặn ${row.ip}?`}
                    description="IP này sẽ gọi API lại được ngay."
                    okText="Gỡ chặn"
                    cancelText="Huỷ"
                    onConfirm={() => handleUnblock(row.ip)}
                >
                    <Button size="small" danger icon={<FontAwesomeIcon icon={faTrash} />}>Gỡ</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            {/* Gọn 1 dòng: nội dung này đọc một lần là nhớ, không cần chiếm chỗ mỗi lần mở tab. */}
            <Alert
                type="info"
                showIcon
                banner
                style={{ marginBottom: 12, padding: "4px 12px" }}
                message={
                    <span style={{ fontSize: 13 }}>
                        IP trong danh sách bị chặn ở mọi endpoint <code>/api</code>. Máy chủ tự từ chối IP hệ thống
                        (VPS chính/dự phòng, loopback, dải nội bộ) và IP bạn đang dùng.
                        {currentIp && <> IP của bạn: <Tag style={{ marginInlineEnd: 0 }}>{currentIp}</Tag></>}
                    </span>
                }
            />

            <Space.Compact style={{ marginBottom: 16, width: "100%", maxWidth: 720 }}>
                <Input
                    style={{ width: 220 }}
                    placeholder="IP cần chặn (vd 18.136.253.152)"
                    value={ip}
                    onChange={(e) => setIp(e.target.value.replace(/[^0-9a-fA-F.:]/g, ""))}
                    onPressEnter={handleBlock}
                />
                <Input
                    placeholder="Ghi chú (vd: bot quét product-commission)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onPressEnter={handleBlock}
                />
                <Button
                    type="primary"
                    danger
                    loading={saving}
                    disabled={!ip.trim()}
                    icon={<FontAwesomeIcon icon={faBan} />}
                    onClick={handleBlock}
                >
                    Chặn
                </Button>
                <Button icon={<FontAwesomeIcon icon={faRotateRight} />} onClick={fetchList} loading={loading} />
            </Space.Compact>

            <Table
                rowKey="ip"
                size="small"
                loading={loading}
                dataSource={entries}
                columns={columns}
                pagination={false}
                locale={{ emptyText: "Chưa chặn IP nào" }}
                footer={entries.length ? () => (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Số lượt chặn gộp mỗi phút nên có thể trễ chút và là ước lượng — dùng để biết
                        chặn có ăn không, không phải số liệu đối soát.
                    </Text>
                ) : undefined}
            />
        </div>
    );
}

export default BlockedIpTab;
